// Sheets (menu/search/cart/chat/filters), theme, header, tabs, gallery, map, shop pagination.
import { $, $$, api, cardHTML, isAr, t } from "./util";
import { renderCart } from "./cart";

let lastFocus = null;

export function openSheet(name) {
	if (name === "filters") {
		const f = $("[data-sheet=filters]");
		f?.classList.add("is-open");
		document.documentElement.style.overflow = "hidden";
		return;
	}
	const el = $(`[data-sheet="${name}"]`);
	if (!el) return;
	$$("[data-sheet]:not([hidden])").forEach((o) => o !== el && o.dataset.sheet !== "filters" && closeSheet(o, true));
	lastFocus = document.activeElement;
	el.hidden = false;
	if (name !== "chat") document.documentElement.style.overflow = "hidden";
	document.dispatchEvent(new CustomEvent("darat:open", { detail: name }));
	if (name === "cart") renderCart();
	setTimeout(() => (el.querySelector("input:not([type=hidden])") || el.querySelector("button, a"))?.focus({ preventScroll: true }), 60);
}

export function closeSheet(el, instant) {
	if (!el || el.hidden) return;
	if (el.dataset.sheet === "filters") { el.classList.remove("is-open"); document.documentElement.style.overflow = ""; return; }
	const done = () => { el.hidden = true; el.classList.remove("is-closing"); document.documentElement.style.overflow = ""; };
	if (instant) return done();
	el.classList.add("is-closing");
	setTimeout(done, 240);
	lastFocus?.focus?.({ preventScroll: true });
}

function initSheets() {
	document.addEventListener("click", (e) => {
		const op = e.target.closest("[data-open]");
		if (op) { e.preventDefault(); openSheet(op.dataset.open); return; }
		const cl = e.target.closest("[data-close]");
		if (cl) { closeSheet(cl.closest("[data-sheet]")); return; }
		if (e.target.matches(".ds-sheet, .ds-search")) closeSheet(e.target);
		const acc = e.target.closest("[data-toggle-account]");
		$$(".ds-account").forEach((a) => a.classList.toggle("is-open", !!acc && a.contains(acc) && !a.classList.contains("is-open")));
	});
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape") $$("[data-sheet]").forEach((s) => (!s.hidden || s.classList.contains("is-open")) && closeSheet(s));
		if (e.key === "/" && !e.target.closest("input, textarea, select, [contenteditable]")) { e.preventDefault(); openSheet("search"); }
	});
}

// two themes: light (default) and dark — dark IS the gamer skin (store-gamer.css, loaded on first use)
const isDarkNow = () => document.documentElement.getAttribute("data-theme") === "dark";

function applyTheme(choice) {
	const h = document.documentElement;
	const dark = choice === "dark";
	const set = () => {
		h.setAttribute("data-theme", dark ? "dark" : "light");
		if (dark) h.setAttribute("data-skin", "gamer"); else h.removeAttribute("data-skin");
		document.querySelectorAll("[data-theme-toggle]").forEach((b) => b.setAttribute("aria-pressed", String(dark)));
	};
	try { localStorage.setItem("darat_theme", choice); } catch (e) { /* private mode */ }
	try { (window.dataLayer = window.dataLayer || []).push({ event: "theme_change", theme: choice }); } catch (e) { /* analytics optional */ }
	const swap = () => (document.startViewTransition && !matchMedia("(prefers-reduced-motion: reduce)").matches ? document.startViewTransition(set) : set());
	if (dark && !document.getElementById("ds-gamer-css") && window.__gamerCss) {
		const l = document.createElement("link");  // fetch the skin first, then switch (no unstyled flash)
		l.rel = "stylesheet"; l.id = "ds-gamer-css"; l.href = window.__gamerCss;
		l.onload = swap; l.onerror = swap;
		document.head.appendChild(l);
	} else swap();
}

// signed-in customer: the header account button opens a menu (account sections + sign out)
function initAcctMenu() {
	const btn = $("[data-acct-menu]"), menu = $("[data-acctmenu]");
	if (!btn || !menu) return;
	document.body.appendChild(menu);  // fixed position, outside the header's stacking context
	const close = () => { menu.hidden = true; btn.setAttribute("aria-expanded", "false"); };
	const open = () => {
		menu.hidden = false;
		const r = btn.getBoundingClientRect(), w = menu.offsetWidth;
		menu.style.top = `${r.bottom + 8}px`;
		menu.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, isAr ? r.left : r.right - w))}px`;
		btn.setAttribute("aria-expanded", "true");
		menu.querySelector("[role=menuitem]")?.focus();
	};
	document.addEventListener("click", async (e) => {
		if (e.target.closest("[data-acct-menu]")) { e.preventDefault(); if (menu.hidden) open(); else close(); return; }
		const out = e.target.closest("[data-acct-signout]");
		if (out) {
			out.disabled = true;
			try { await api("logout", {}, "POST"); } catch (ex) { /* already signed out */ }
			location.href = "/";
			return;
		}
		if (!menu.hidden && !e.target.closest("[data-acctmenu]")) close();
		if (e.target.closest("[data-acctmenu] a")) close();
	});
	document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !menu.hidden) { close(); btn.focus(); } });
	addEventListener("scroll", close, { passive: true });
	addEventListener("resize", close);
}

function initTheme() {
	// one click switches light <-> dark
	document.addEventListener("click", (e) => {
		if (!e.target.closest("[data-theme-toggle]")) return;
		e.preventDefault();
		applyTheme(isDarkNow() ? "light" : "dark");
	});
	document.querySelectorAll("[data-theme-toggle]").forEach((b) => b.setAttribute("aria-pressed", String(isDarkNow())));
}

function initHeader() {
	const header = $("[data-header]");
	if (!header) return;
	// Compacting shrinks the header (~60px), which moves the scroll position. A wide
	// gap between the two thresholds stops it flipping back and forth (flashing),
	// especially on short pages like the order confirmation.
	let compact = false;
	const update = () => {
		const y = scrollY;
		if (!compact && y > 180) compact = true;
		else if (compact && y < 30) compact = false;
		header.classList.toggle("is-compact", compact);
	};
	addEventListener("scroll", update, { passive: true });
	update();
}

function initFabAway() {
	// the floating assistant would cover the language switch / back-to-top at the very bottom
	const fab = $(".ds-fab"), legal = $(".ds-footer__legal");
	if (!fab || !legal || !("IntersectionObserver" in window)) return;
	new IntersectionObserver(([e]) => fab.classList.toggle("is-away", e.isIntersecting)).observe(legal);
}

function initTabs() {
	$$("[data-tabs]").forEach((box) => {
		box.addEventListener("click", (e) => {
			const tab = e.target.closest("[data-tab]");
			if (!tab) return;
			$$("[data-tab]", box).forEach((b) => b.setAttribute("aria-selected", b === tab ? "true" : "false"));
			$$("[data-panel]", box).forEach((p) => (p.hidden = p.dataset.panel !== tab.dataset.tab));
		});
	});
}

function initGallery() {
	const g = $("[data-gallery]");
	if (!g) return;
	const main = $("[data-gallery-main]", g);
	g.addEventListener("click", (e) => {
		const th = e.target.closest("[data-thumb]");
		if (th && main) { main.src = th.dataset.thumb; $$("[data-thumb]", g).forEach((b) => b.classList.toggle("is-on", b === th)); }
	});
	const zoom = $("[data-zoom]", g);
	if (zoom && main) {
		zoom.addEventListener("click", () => zoom.classList.toggle("is-zoom"));
		zoom.addEventListener("mousemove", (e) => {
			const r = zoom.getBoundingClientRect();
			zoom.style.setProperty("--zx", ((e.clientX - r.left) / r.width) * 100 + "%");
			zoom.style.setProperty("--zy", ((e.clientY - r.top) / r.height) * 100 + "%");
		});
		zoom.addEventListener("mouseleave", () => zoom.classList.remove("is-zoom"));
	}
	const sticky = $("[data-stickybuy]");
	const buy = $("[data-buy]");
	if (sticky && buy && "IntersectionObserver" in window) {
		new IntersectionObserver(([en]) => sticky.classList.toggle("is-on", !en.isIntersecting && en.boundingClientRect.top < 0)).observe(buy);
	}
}

function initMaps() {
	const maps = $$("[data-map]");
	if (!maps.length || !("IntersectionObserver" in window)) return;
	const io = new IntersectionObserver((entries) => entries.forEach((en) => {
		if (!en.isIntersecting) return;
		const el = en.target;
		io.unobserve(el);
		const f = document.createElement("iframe");
		f.src = el.dataset.map; f.loading = "lazy"; f.title = t("خريطة المتجر", "Store map"); f.referrerPolicy = "no-referrer-when-downgrade";
		el.appendChild(f);
	}), { rootMargin: "200px" });
	maps.forEach((m) => io.observe(m));
}

function initShop() {
	const grid = $("[data-grid]");
	const more = $("[data-load-more]");
	if (!grid || !more) return;
	const params = JSON.parse(grid.dataset.params || "{}");
	let next = parseInt(grid.dataset.next || "0", 10);
	let busy = false;
	const shown = $("[data-shown]");
	async function load() {
		if (busy || !next) return;
		busy = true;
		more.classList.add("is-loading");
		const skel = Array.from({ length: 4 }, () => { const d = document.createElement("div"); d.className = "ds-skel"; grid.appendChild(d); return d; });
		try {
			const res = await api("darat.storefront.api.products", { ...params, start: next, page_len: 24 });
			skel.forEach((s) => s.remove());
			grid.insertAdjacentHTML("beforeend", res.items.map(cardHTML).join(""));
			document.dispatchEvent(new CustomEvent("darat:content", { detail: grid }));
			next = res.start + res.items.length < res.total ? res.start + res.items.length : 0;
			if (shown) shown.textContent = `${t("عرض", "Showing")} ${grid.querySelectorAll(".ds-card").length} / ${res.total}`;
			if (!next) more.remove();
			const url = new URL(location.href); url.searchParams.set("page", Math.ceil((res.start + res.items.length) / 24));
			history.replaceState(null, "", url);
		} catch (e) { skel.forEach((s) => s.remove()); }
		busy = false;
		more.classList.remove("is-loading");
	}
	more.addEventListener("click", (e) => { e.preventDefault(); load(); });
	if ("IntersectionObserver" in window) new IntersectionObserver(([en]) => en.isIntersecting && load(), { rootMargin: "600px" }).observe(more);
}

function initToTop() {
	document.addEventListener("click", (e) => {
		if (!e.target.closest("[data-totop]")) return;
		if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 }); else window.scrollTo({ top: 0, behavior: "smooth" });
	});
}

function initFooter() {
	const small = window.matchMedia("(max-width: 640px)");
	const cols = $$(".ds-fcol");
	const sync = () => cols.forEach((d) => (d.open = !small.matches));
	sync();
	small.addEventListener?.("change", sync);
	cols.forEach((d) => d.querySelector("summary")?.addEventListener("click", (e) => { if (!small.matches) e.preventDefault(); }));
}

export function initUI() {
	initToTop();
	initFooter();
	initSheets(); initTheme(); initAcctMenu(); initHeader(); initFabAway(); initTabs(); initGallery(); initMaps(); initShop();
}
